import { FC, useCallback, useMemo, useRef, useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from 'react-simple-maps';
import clsx from 'clsx';
import { CHART_COLORS } from './chartConfig';
import { alpha2ToNumeric } from './countryCodeMap';
import MapTooltip from './MapTooltip';

import {
  WORLD_MAP_GEO_URL,
  WORLD_MAP_HEIGHT,
  WORLD_MAP_PROJECTION_CONFIG,
  WORLD_MAP_WIDTH,
} from './worldMapLayout';

export interface CountryDataItem {
  countryCode: string;
  value: number;
}

const SCALE_FLOOR_COLOR = '#c9d4fb';

function hexToRgb(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '');
  const value = parseInt(normalized, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function interpolateHexColor(from: string, to: string, ratio: number): string {
  const clamped = Math.min(Math.max(ratio, 0), 1);
  const [fromR, fromG, fromB] = hexToRgb(from);
  const [toR, toG, toB] = hexToRgb(to);
  const channel = (start: number, end: number) =>
    Math.round(start + (end - start) * clamped);
  const toHex = (component: number) => component.toString(16).padStart(2, '0');
  return `#${toHex(channel(fromR, toR))}${toHex(channel(fromG, toG))}${toHex(
    channel(fromB, toB),
  )}`;
}

interface WorldMapChartProps {
  data: CountryDataItem[];
  highlightColor?: string;
  baseColor?: string;
  strokeColor?: string;
  height?: number | string;
  className?: string;
  /** When true, map can be panned (drag) and zoomed (scroll / pinch). */
  zoomable?: boolean;
  minZoom?: number;
  maxZoom?: number;
  /** When true (default), shows a low→high color-scale legend overlay. */
  showScaleLegend?: boolean;
}

const scaleValueFormatter = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

interface TooltipState {
  countryName: string;
  value: number;
  x: number;
  y: number;
}

const WorldMapChart: FC<WorldMapChartProps> = ({
  data,
  highlightColor = CHART_COLORS.defaultDark,
  baseColor = CHART_COLORS.mapBase,
  strokeColor = '#ffffff',
  height = 200,
  className,
  zoomable = false,
  minZoom = 1,
  maxZoom = 8,
  showScaleLegend = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [zoom, setZoom] = useState(1);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev * 2, maxZoom));
  }, [maxZoom]);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev / 2, minZoom));
  }, [minZoom]);

  const dataByNumericId = useMemo(() => {
    const map = new Map<string, CountryDataItem>();
    data.forEach((item) => {
      const numericId = alpha2ToNumeric(item.countryCode);
      if (numericId) {
        map.set(numericId, item);
      }
    });
    return map;
  }, [data]);

  const maxValue = useMemo(
    () => data.reduce((max, item) => Math.max(max, item.value), 0),
    [data],
  );

  const colorForValue = useCallback(
    (value: number) => {
      const ratio = maxValue > 0 ? value / maxValue : 0;
      return interpolateHexColor(SCALE_FLOOR_COLOR, highlightColor, ratio);
    },
    [maxValue, highlightColor],
  );

  const handleMouseInteraction = useCallback(
    (
      geo: { id: string; properties: { name: string } },
      event: React.MouseEvent,
    ) => {
      const item = dataByNumericId.get(geo.id);
      if (!item || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      setTooltip({
        countryName: geo.properties.name,
        value: item.value,
        x: event.clientX - rect.left,
        y: event.clientY - rect.top - 8,
      });
    },
    [dataByNumericId],
  );

  const handleMouseLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  const geographies = (
    <Geographies geography={WORLD_MAP_GEO_URL}>
      {({ geographies: geoList }) =>
        geoList.map((geo) => {
          const item = dataByNumericId.get(geo.id);
          const fill = item ? colorForValue(item.value) : baseColor;
          const hoverFill = item
            ? interpolateHexColor(fill, '#000000', 0.15)
            : CHART_COLORS.mapHoverBase;
          return (
            <Geography
              key={geo.rsmKey}
              geography={geo}
              fill={fill}
              stroke={strokeColor}
              strokeWidth={0.2}
              style={{
                default: { outline: 'none' },
                hover: {
                  outline: 'none',
                  fill: hoverFill,
                },
                pressed: { outline: 'none' },
              }}
              onMouseEnter={(event) => handleMouseInteraction(geo, event)}
              onMouseMove={(event) => handleMouseInteraction(geo, event)}
              onMouseLeave={handleMouseLeave}
            />
          );
        })
      }
    </Geographies>
  );

  return (
    <div
      ref={containerRef}
      className={clsx('tw-group tw-relative tw-w-full', className)}
      style={{
        height: typeof height === 'number' ? `${height}px` : height,
        ...(zoomable ? { cursor: 'grab' } : {}),
      }}
      data-cy="analytics-chart-world-map"
    >
      <ComposableMap
        projectionConfig={WORLD_MAP_PROJECTION_CONFIG}
        width={WORLD_MAP_WIDTH}
        height={WORLD_MAP_HEIGHT}
        className="tw-h-full tw-w-full"
      >
        {zoomable ? (
          <ZoomableGroup
            center={WORLD_MAP_PROJECTION_CONFIG.center}
            zoom={zoom}
            minZoom={minZoom}
            maxZoom={maxZoom}
            translateExtent={[
              [0, 0],
              [WORLD_MAP_WIDTH, WORLD_MAP_HEIGHT],
            ]}
          >
            {geographies}
          </ZoomableGroup>
        ) : (
          geographies
        )}
      </ComposableMap>
      {zoomable && (
        <div className="tw-border-gray-200 tw-absolute tw-bottom-2 tw-right-2 tw-flex tw-flex-col tw-overflow-hidden tw-rounded tw-border tw-opacity-0 tw-shadow-sm tw-transition-opacity group-hover:tw-opacity-100">
          {[
            { label: '+', onClick: handleZoomIn, disabled: zoom >= maxZoom },
            { label: '−', onClick: handleZoomOut, disabled: zoom <= minZoom },
          ].map(({ label, onClick, disabled }) => (
            <button
              key={label}
              onClick={onClick}
              disabled={disabled}
              className={clsx(
                'tw-text-gray-600 tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-bg-white tw-text-xs tw-font-medium tw-transition-colors',
                'hover:tw-bg-gray-50 disabled:tw-text-gray-300 disabled:tw-cursor-not-allowed',
                'first:tw-border-gray-200 first:tw-border-b',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {showScaleLegend && maxValue > 0 && (
        <div
          className="tw-absolute tw-bottom-2 tw-left-2 tw-flex tw-items-center tw-gap-2 tw-rounded tw-bg-white/80 tw-px-2 tw-py-1"
          data-cy="analytics-chart-world-map_scale-legend"
        >
          <span className="tw-text-[11px] tw-leading-4 tw-text-grayscale-100">
            0
          </span>
          <span
            className="tw-h-2 tw-w-16 tw-rounded"
            style={{
              background: `linear-gradient(to right, ${SCALE_FLOOR_COLOR}, ${highlightColor})`,
            }}
          />
          <span className="tw-text-[11px] tw-leading-4 tw-text-grayscale-100">
            {scaleValueFormatter.format(maxValue)}
          </span>
        </div>
      )}
      {tooltip && (
        <MapTooltip
          countryName={tooltip.countryName}
          value={tooltip.value}
          position={{ x: tooltip.x, y: tooltip.y }}
        />
      )}
    </div>
  );
};

export default WorldMapChart;
